import { useState, useRef } from "react";
import { Plus, X } from "lucide-react";
import { PRODUCT_FORM } from "@/constants/testIds";

const Field = ({ label, required, children, className = "" }) => (
  <div className={className}>
    <label className="block text-xs font-medium text-slate-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const Input = (props) => (
  <input
    {...props}
    className={`w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all text-sm ${props.className || ""}`}
  />
);

const Select = ({ children, ...props }) => (
  <select
    {...props}
    className={`w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all text-sm ${props.className || ""}`}
  >
    {children}
  </select>
);

function TagInput({ tags, onChange, placeholder }) {
  const [input, setInput] = useState("");
  const inputRef = useRef(null);

  const add = (val) => {
    const v = val.trim();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    setInput("");
  };

  const remove = (t) => onChange(tags.filter(x => x !== t));

  return (
    <div className="tag-input-container" onClick={() => inputRef.current?.focus()}>
      {tags.map(t => (
        <span key={t} className="tag-item">
          {t}
          <button type="button" onClick={() => remove(t)}><X className="w-3 h-3" /></button>
        </span>
      ))}
      <input
        ref={inputRef}
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(input); }
          if (e.key === "Backspace" && !input) onChange(tags.slice(0, -1));
        }}
        onBlur={() => { if (input.trim()) add(input); }}
        placeholder={tags.length === 0 ? placeholder : ""}
        className="flex-1 min-w-20 outline-none text-sm text-slate-900 bg-transparent"
        style={{ minWidth: "80px" }}
      />
    </div>
  );
}

const SECTION = ({ title, children }) => (
  <div className="mt-6">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-1 h-4 bg-orange-500 rounded-full" />
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      <div className="flex-1 h-px bg-slate-200" />
    </div>
    {children}
  </div>
);

export default function BasicInfoTab({ data, onChange }) {
  return (
    <div>
      {/* Section header */}
      <div className="flex items-center gap-2 mb-5">
        <div className="w-5 h-5 bg-orange-100 rounded flex items-center justify-center">
          <span className="text-orange-600 text-xs font-bold">1</span>
        </div>
        <h2 className="text-base font-semibold text-slate-900">Basic Information</h2>
      </div>

      {/* Row 1: Item Name + Product Type */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <Field label="Item Name" required>
            <Input
              data-testid={PRODUCT_FORM.itemNameInput}
              value={data.item_name}
              onChange={e => onChange("item_name", e.target.value)}
              placeholder="Enter product name"
            />
          </Field>
        </div>
        <div>
          <Field label="Product Type" required>
            <div className="flex gap-2">
              <Input
                data-testid={PRODUCT_FORM.productTypeInput}
                value={data.product_type}
                onChange={e => onChange("product_type", e.target.value)}
                placeholder="e.g. Stationery"
              />
            </div>
            <button
              type="button"
              className="mt-1 text-xs text-orange-600 hover:text-orange-700 flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Create New Type
            </button>
          </Field>
        </div>
      </div>

      {/* Collection */}
      <div className="mt-4">
        <Field label="Collection">
          <TagInput
            tags={data.collection}
            onChange={v => onChange("collection", v)}
            placeholder="Add collection and press Enter..."
          />
          <button type="button" className="mt-1 text-xs text-orange-600 hover:text-orange-700 flex items-center gap-1">
            <Plus className="w-3 h-3" /> Create New Collection
          </button>
        </Field>
      </div>

      {/* Brand + Highlight */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <Field label="Brand Name">
          <Input
            data-testid={PRODUCT_FORM.brandNameInput}
            value={data.brand_name}
            onChange={e => onChange("brand_name", e.target.value)}
            placeholder="e.g. Clickedin"
          />
        </Field>
        <Field label="Item Highlight">
          <Input
            value={data.item_highlight}
            onChange={e => onChange("item_highlight", e.target.value)}
            placeholder="Brief highlight for the product"
          />
        </Field>
      </div>

      {/* Product ID + SKU + Status + URL Slug */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
        <Field label="Product ID" >
          <Input
            data-testid="product-product-id-input"
            value={data.product_id || ""}
            onChange={e => onChange("product_id", e.target.value)}
            placeholder="e.g. PROD001"
          />
          <p className="text-xs text-slate-400 mt-1">Groups variant SKUs together</p>
        </Field>
        <Field label="SKU" required>
          <Input
            data-testid={PRODUCT_FORM.skuInput}
            value={data.product_sku}
            onChange={e => onChange("product_sku", e.target.value)}
            placeholder="e.g. CLKD-A4-YLW"
          />
        </Field>
        <Field label="Status">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${data.status === "Live" ? "bg-green-500" : "bg-amber-400"}`} />
              <Select
                data-testid={PRODUCT_FORM.statusSelect}
                value={data.status}
                onChange={e => onChange("status", e.target.value)}
              >
                <option value="Live">Live</option>
                <option value="Draft">Draft</option>
                <option value="Archived">Archived</option>
              </Select>
            </div>
          </div>
        </Field>
        <Field label="URL Slug">
          <Input
            value={data.url_slug}
            onChange={e => onChange("url_slug", e.target.value)}
            placeholder="clickedin-notepad-a4"
          />
        </Field>
      </div>

      {/* Search Keywords */}
      <div className="mt-4">
        <Field label="Search Keywords">
          <TagInput
            tags={data.search_keywords}
            onChange={v => onChange("search_keywords", v)}
            placeholder="Add keyword and press Enter..."
          />
        </Field>
      </div>

      {/* Classification & Tax */}
      <SECTION title="Classification & Tax Details">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="HSN Code" required>
            <Input
              value={data.hsn_code}
              onChange={e => onChange("hsn_code", e.target.value)}
              placeholder="e.g. 4820101X"
            />
          </Field>
          <Field label="Tax Rate (%)">
            <Select value={data.tax_rate} onChange={e => onChange("tax_rate", Number(e.target.value))}>
              {[0, 5, 12, 18, 28].map(r => (
                <option key={r} value={r}>{r}%</option>
              ))}
            </Select>
          </Field>
          <Field label="Country of Origin">
            <Input
              value={data.country_of_origin}
              onChange={e => onChange("country_of_origin", e.target.value)}
              placeholder="India"
            />
          </Field>
          <Field label="Manufacturer">
            <Input
              value={data.manufacturer}
              onChange={e => onChange("manufacturer", e.target.value)}
              placeholder="Manufacturer name"
            />
          </Field>
        </div>
      </SECTION>

      {/* Pricing */}
      <SECTION title="Pricing">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="MRP (₹)">
            <Input
              data-testid={PRODUCT_FORM.mrpInput}
              type="number"
              min="0"
              value={data.mrp || ""}
              onChange={e => onChange("mrp", Number(e.target.value))}
              placeholder="0"
            />
          </Field>
          <Field label="Deal Price excl. GST (₹)">
            <Input
              data-testid={PRODUCT_FORM.dealPriceInput}
              type="number"
              min="0"
              value={data.deal_price_excl_gst || ""}
              onChange={e => onChange("deal_price_excl_gst", Number(e.target.value))}
              placeholder="0"
            />
          </Field>
          <Field label="Deal Price incl. GST (₹)">
            <Input
              type="number"
              min="0"
              value={data.deal_price_incl_gst || ""}
              onChange={e => onChange("deal_price_incl_gst", Number(e.target.value))}
              placeholder="0"
            />
          </Field>
          <Field label="Star Rating">
            <Input
              type="number"
              min="0"
              max="5"
              step="0.1"
              value={data.star_rating || ""}
              onChange={e => onChange("star_rating", Number(e.target.value))}
              placeholder="4.0"
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <Field label="No. of Reviews">
            <Input
              type="number"
              min="0"
              value={data.num_reviews || ""}
              onChange={e => onChange("num_reviews", Number(e.target.value))}
              placeholder="0"
            />
          </Field>
          <Field label="Offers" className="md:col-span-3">
            <textarea
              value={data.offers || ""}
              onChange={e => onChange("offers", e.target.value)}
              rows={3}
              placeholder={"Free shipping on orders above ₹500\nBank offer: 10% off with HDFC credit card\nGST invoice available — save 18%"}
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            />
            <p className="text-xs text-slate-400 mt-1">One offer per line — each line shows as a separate offer on the product page</p>
          </Field>
        </div>
      </SECTION>

      {/* Special Offer */}
      <SECTION title="Special Offer (Countdown Banner)">
        <p className="text-xs text-slate-400 mb-3">Shown as an urgency banner on the product page. Leave blank to hide it.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Offer Label" className="md:col-span-2">
            <Input
              value={data.special_offer_label || ""}
              onChange={e => onChange("special_offer_label", e.target.value)}
              placeholder="Extra 10% OFF on orders above ₹2,000"
            />
          </Field>
          <Field label="Coupon Code">
            <Input
              value={data.special_offer_code || ""}
              onChange={e => onChange("special_offer_code", e.target.value.toUpperCase())}
              placeholder="SAVE10"
              className="font-mono"
            />
          </Field>
          <Field label="Timer (hours)">
            <Input
              type="number"
              min="0"
              max="72"
              value={data.special_offer_timer_hours || ""}
              onChange={e => onChange("special_offer_timer_hours", Number(e.target.value))}
              placeholder="24"
            />
          </Field>
        </div>
      </SECTION>

      {/* Coupon Discount */}
      <SECTION title="Coupon Discount Strip">
        <p className="text-xs text-slate-400 mb-3">
          Shown below the price as a coupon badge. E.g. "5%" → displays "Coupon Discount <green>Save 5%</green> now." Leave blank to hide.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Field label="Coupon Value (e.g. 5% or ₹50 off)">
            <Input
              value={data.coupon_label || ""}
              onChange={e => onChange("coupon_label", e.target.value)}
              placeholder="5%"
            />
          </Field>
          <Field label="Details Link (optional)">
            <Input
              value={data.coupon_details_url || ""}
              onChange={e => onChange("coupon_details_url", e.target.value)}
              placeholder="https://..."
            />
          </Field>
          <Field label="Apply Button Text">
            <Input
              value={data.coupon_button_text || ""}
              onChange={e => onChange("coupon_button_text", e.target.value)}
              placeholder="Apply Coupon"
            />
          </Field>
          <Field label="Coupon Code to apply">
            <Input
              value={data.coupon_code || ""}
              onChange={e => onChange("coupon_code", e.target.value.toUpperCase())}
              placeholder="SAVE5 (must exist under Coupons)"
            />
          </Field>
        </div>
      </SECTION>

      {/* Other Details */}
      <SECTION title="Other Details">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Continue selling when out of stock">
            <select
              value={data.continue_selling_oos === true ? "yes" : data.continue_selling_oos === false ? "no" : ""}
              onChange={e => onChange("continue_selling_oos", e.target.value === "yes" ? true : e.target.value === "no" ? false : null)}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-orange-400"
              data-testid="continue-selling-select"
            >
              <option value="">Not set (default)</option>
              <option value="yes">Yes - keep selling, show out-of-stock dispatch time</option>
              <option value="no">No - stop at available stock</option>
            </select>
          </Field>
          <Field label="Package Includes">
            <Input
              value={data.package_includes}
              onChange={e => onChange("package_includes", e.target.value)}
              placeholder="1 x Notepad"
            />
          </Field>
          <Field label="Delivery Timeline (Days)">
            <Input
              value={data.delivery_timeline}
              onChange={e => onChange("delivery_timeline", e.target.value)}
              placeholder="2-5"
            />
          </Field>
          <Field label="Shipping Weight (grams)">
            <Input
              type="number"
              min="0"
              step="1"
              value={data.shipping_weight || ""}
              onChange={e => onChange("shipping_weight", Number(e.target.value))}
              placeholder="250"
            />
          </Field>
          <Field label="Product Dimension (cm)">
            <Input
              value={data.product_dimension}
              onChange={e => onChange("product_dimension", e.target.value)}
              placeholder="21 x 29.7 x 1"
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <Field label="Material">
            <Input
              value={data.material}
              onChange={e => onChange("material", e.target.value)}
              placeholder="Paper"
            />
          </Field>
          <Field label="Color">
            <Input
              value={data.color}
              onChange={e => onChange("color", e.target.value)}
              placeholder="Yellow"
            />
          </Field>
          <Field label="Electrical / Non-Electrical">
            <Select value={data.electrical} onChange={e => onChange("electrical", e.target.value)}>
              <option value="Non-Electrical">Non-Electrical</option>
              <option value="Electrical">Electrical</option>
            </Select>
          </Field>
          <Field label="Return Policy">
            <Select value={data.return_policy} onChange={e => onChange("return_policy", e.target.value)}>
              <option value="Non-returnable">Non-returnable</option>
              <option value="7 days return">7 days return</option>
              <option value="15 days return">15 days return</option>
              <option value="30 days return">30 days return</option>
            </Select>
          </Field>
        </div>
      </SECTION>
    </div>
  );
}
